import Box from '@mui/material/Box';
import { t } from 'i18next';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AttachmentDropOverlay from '~components/ComposerAttachments/AttachmentDropOverlay';
import { FileReceiver } from '~hooks/useComposerAttachments/useComposerAttachments';
import useFileDropTarget from '~hooks/useComposerAttachments/useFileDropTarget';
import { Nullable } from '~types/Nullable';

type MessagingAttachmentDropContextValue = {
  setFileReceiver: (receiver: Nullable<FileReceiver>) => void;
};

const MessagingAttachmentDropContext =
  createContext<Nullable<MessagingAttachmentDropContextValue>>(null);

// The drop area is the whole pane, not the composer, and the composer that owns the files is
// keyed per channel deep inside it, so it registers its receiver upward.
export function useMessagingAttachmentDropTarget(receiver: Nullable<FileReceiver>) {
  const context = useContext(MessagingAttachmentDropContext);
  const setFileReceiver = context?.setFileReceiver;
  const receiverRef = useRef(receiver);
  receiverRef.current = receiver;
  const canReceive = Boolean(receiver);

  // Registered through the ref so a receiver rebuilt on every render does not re-render the pane.
  useEffect(() => {
    if (!setFileReceiver) return undefined;

    setFileReceiver(canReceive ? (files) => receiverRef.current?.(files) : null);

    return () => setFileReceiver(null);
  }, [canReceive, setFileReceiver]);
}

type MessagingAttachmentDropZoneProps = {
  children: React.ReactNode;
};

export default function MessagingAttachmentDropZone({
  children,
}: MessagingAttachmentDropZoneProps) {
  const [fileReceiver, setFileReceiverState] = useState<Nullable<FileReceiver>>(null);
  const { dropTargetProps, isDraggingOver } = useFileDropTarget(fileReceiver);

  const setFileReceiver = useCallback((receiver: Nullable<FileReceiver>) => {
    setFileReceiverState(() => receiver);
  }, []);

  const contextValue = useMemo(() => ({ setFileReceiver }), [setFileReceiver]);

  return (
    <MessagingAttachmentDropContext.Provider value={contextValue}>
      <Box
        data-test-id="messaging-attachment-drop-zone"
        {...dropTargetProps}
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
        }}
      >
        {children}

        {isDraggingOver && (
          <AttachmentDropOverlay label={t('messaging.drop_to_attach')} />
        )}
      </Box>
    </MessagingAttachmentDropContext.Provider>
  );
}
